import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import { agentService } from "../../../../services/agentService";

/* ===================== TYPES ===================== */

export type IntentExample = {
  id: string;
  text: string;
};

export type CapabilityIntent = {
  id: string;
  capabilityCode: string;
  description?: string;
  examples: IntentExample[];
};

export type AgentTypeDTO = {
  id: string;
  code: string;
  name: string;
  description?: string;
  isActive: boolean;
  metadata: string;
  version: number;
  capabilities: CapabilityIntent[];
};

type Props = {
  isOpen: boolean;
  isEditing: boolean;
  agentTypeDetails?: AgentTypeDTO;
  onClose: () => void;
  refreshList: () => void;
};

/* ===================== COMPONENT ===================== */

const AgentTypeModal: React.FC<Props> = ({
  isOpen,
  isEditing,
  agentTypeDetails,
  onClose,
  refreshList,
}) => {
  const [agentType, setAgentType] = useState<AgentTypeDTO>({
    id: "",
    code: "",
    name: "",
    description: "",
    isActive: true,
    metadata: "{}",
    version: 1,
    capabilities: [],
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* ---------- Edit mode ---------- */
  useEffect(() => {
    if (!agentTypeDetails) return;

    setAgentType({
      ...agentTypeDetails,
      metadata:
        typeof agentTypeDetails.metadata === "string"
          ? agentTypeDetails.metadata
          : JSON.stringify(agentTypeDetails.metadata, null, 2),

      capabilities: agentTypeDetails.capabilities.map((cap) => ({
        id: cap.id ?? uuidv4(),
        capabilityCode: cap.capabilityCode,
        description: cap.description ?? "",
        examples: (cap.examples ?? []).map((ex: any) => ({
          id: uuidv4(),
          text: typeof ex === "string" ? ex : ex.text,
        })),
      })),
    });
  }, [agentTypeDetails]);

  /* ---------- Handlers ---------- */
  const updateField = (name: keyof AgentTypeDTO, value: any) => {
    setError(null);
    setAgentType((prev) => ({ ...prev, [name]: value }));
  };

  /* ---------- Save ---------- */
  const handleSave = async () => {
    if (!agentType.code.trim()) return setError("Agent code is required");
    if (!agentType.name.trim()) return setError("Agent name is required");

    for (const cap of agentType.capabilities) {
      if (!cap.capabilityCode.trim()) {
        return setError("Each intent must have a code");
      }
      if (!cap.examples.length) {
        return setError(
          `Intent "${cap.capabilityCode}" must have at least one example`
        );
      }
    }

    let metadataObj: any;
    try {
      metadataObj = agentType.metadata
        ? JSON.parse(agentType.metadata)
        : undefined;
    } catch {
      return setError("Metadata must be valid JSON");
    }

    const payload = {
      code: agentType.code,
      name: agentType.name,
      description: agentType.description,
      isActive: agentType.isActive,
      version: agentType.version,
      metadata: metadataObj,
      capabilities: agentType.capabilities.map((c) => ({
        capabilityCode: c.capabilityCode,
        description: c.description,
        examples: c.examples.map((e) => e.text),
      })),
    };

    try {
      setLoading(true);
      console.log("Payload to save:", payload);
      isEditing
        ? await agentService.updateAgentType(agentType.id, payload)
        : await agentService.createAgentType(payload);

      toast.success(isEditing ? "Agent Type Updated" : "Agent Type Created");
      await refreshList();
      onClose();
    } catch (e) {
      console.error(e);
      toast.error("Failed to save Agent Type");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  /* ===================== UI ===================== */

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={() => {
          if (!loading && window.confirm("Discard changes?")) onClose();
        }}
      />

      <div className="relative w-[70vw] max-h-[85vh] overflow-y-auto bg-white rounded-xl shadow-xl p-6 space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-xl font-semibold">
            {isEditing ? "Edit Agent Type" : "Create Agent Type"}
          </h2>
          <p className="text-sm text-gray-500">
            Configure how this agent understands user intent
          </p>
        </div>

        {error && (
          <div className="text-sm text-red-700 bg-red-50 border p-2 rounded">
            {error}
          </div>
        )}

        {/* Basic Info */}
        <section className="grid grid-cols-2 gap-4">
          <Input
            label="Agent Code"
            value={agentType.code}
            onChange={(v) => updateField("code", v.toUpperCase())}
          />
          <Input
            label="Agent Name"
            value={agentType.name}
            onChange={(v) => updateField("name", v)}
          />
        </section>

        <Textarea
          label="Description"
          value={agentType.description ?? ""}
          onChange={(v) => updateField("description", v)}
        />

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={agentType.isActive}
            onChange={(e) => updateField("isActive", e.target.checked)}
          />
          Active
        </label>

        {/* Capabilities */}
        <CapabilitiesEditor
          capabilities={agentType.capabilities}
          setCapabilities={(caps) => updateField("capabilities", caps)}
        />

        {/* Metadata */}
        <Textarea
          label="Agent Metadata (JSON)"
          mono
          value={agentType.metadata}
          onChange={(v) => updateField("metadata", v)}
        />

        {/* Footer */}
        <div className="flex justify-end gap-3">
          <button
            className="border px-4 py-2 rounded"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? "Saving..." : isEditing ? "Save Changes" : "Create Agent"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ===================== CAPABILITIES ===================== */

const CapabilitiesEditor = ({
  capabilities,
  setCapabilities,
}: {
  capabilities: CapabilityIntent[];
  setCapabilities: (c: CapabilityIntent[]) => void;
}) => {
  const update = (id: string, patch: Partial<CapabilityIntent>) =>
    setCapabilities(
      capabilities.map((c) => (c.id === id ? { ...c, ...patch } : c))
    );

  const removeIntent = (id: string) => {
    if (!window.confirm("Remove this intent?")) return;
    setCapabilities(capabilities.filter((c) => c.id !== id));
  };

  return (
    <section className="space-y-4">
      <h3 className="font-semibold text-lg">Intents (Capabilities)</h3>
      <p className="text-sm text-gray-500">
        Each intent represents a possible user goal. Examples are embedded and
        stored in the vector database.
      </p>

      {capabilities.map((cap, idx) => (
        <div key={cap.id} className="border rounded-lg p-4 space-y-3">
          <div className="flex justify-between items-center">
            <h4 className="font-medium">Intent {idx + 1}</h4>
            <button
              className="text-red-600 text-sm"
              onClick={() => removeIntent(cap.id)}
            >
              Remove Intent
            </button>
          </div>

          <Input
            label="Intent Code"
            value={cap.capabilityCode}
            onChange={(v) => update(cap.id, { capabilityCode: v })}
          />

          <Textarea
            label="Intent Description"
            value={cap.description ?? ""}
            onChange={(v) => update(cap.id, { description: v })}
          />

          <ExamplesEditor
            examples={cap.examples}
            setExamples={(ex) => update(cap.id, { examples: ex })}
          />
        </div>
      ))}

      <button
        onClick={() =>
          setCapabilities([
            ...capabilities,
            {
              id: uuidv4(),
              capabilityCode: "",
              description: "",
              examples: [{ id: uuidv4(), text: "" }],
            },
          ])
        }
        className="border border-dashed px-4 py-2 rounded"
      >
        + Add Intent
      </button>
    </section>
  );
};

/* ===================== EXAMPLES ===================== */

const ExamplesEditor = ({
  examples,
  setExamples,
}: {
  examples: IntentExample[];
  setExamples: (e: IntentExample[]) => void;
}) => (
  <div>
    <p className="text-sm font-medium">Training Examples</p>
    <p className="text-xs text-gray-500 mb-2">
      These sentences are embedded and used for intent matching
    </p>

    {examples.map((ex) => (
      <div key={ex.id} className="flex gap-2 mb-2">
        <input
          className="flex-1 border px-2 py-1 rounded"
          value={ex.text}
          onChange={(e) =>
            setExamples(
              examples.map((x) =>
                x.id === ex.id ? { ...x, text: e.target.value } : x
              )
            )
          }
        />
        <button
          onClick={() => setExamples(examples.filter((x) => x.id !== ex.id))}
        >
          ✕
        </button>
      </div>
    ))}

    <button
      className="text-sm text-blue-600"
      onClick={() =>
        setExamples([...examples, { id: uuidv4(), text: "" }])
      }
    >
      + Add example
    </button>
  </div>
);

/* ===================== PRIMITIVES ===================== */

const Input = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) => (
  <div>
    <label className="text-sm font-medium">{label}</label>
    <input
      className="w-full border px-3 py-2 rounded"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

const Textarea = ({
  label,
  value,
  onChange,
  mono,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  mono?: boolean;
}) => (
  <div>
    <label className="text-sm font-medium">{label}</label>
    <textarea
      className={`w-full border px-3 py-2 rounded ${mono ? "font-mono text-sm" : ""
        }`}
      rows={4}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

export default AgentTypeModal;
